import React, { useRef, useState, useEffect } from "react";
import { Popper, PopperProps } from "@material-ui/core";
import { createObjectID } from "@bonliva-crm/common";

type PopperContentProps = {
  opened: boolean;
  open: () => void;
  close: () => void;
  closeWithDelay: () => void;
};

type Props = {
  component?: React.ElementType;
  className?: string;
  popperContent: React.ReactNode | ((props: PopperContentProps) => React.ReactNode);
  children: React.ReactNode | ((props: PopperContentProps) => React.ReactNode);
  PopperProps?: Partial<PopperProps>;
  placement?: PopperProps["placement"];
  rootStyle?: React.CSSProperties;
};

export function WithPopperSimple(props: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [id] = useState(createObjectID());
  let timer: ReturnType<typeof setTimeout> | null = null;

  useEffect(() => {
    const handlePopperOpen = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail.sender !== ref.current) {
        setOpen(false);
      }
    };

    window.addEventListener("onPopperOpen", handlePopperOpen);

    return () => {
      window.removeEventListener("onPopperOpen", handlePopperOpen);
    };
  }, []);

  const popperProps: Partial<PopperProps> = {};

  if (props.placement) {
    popperProps.placement = props.placement;
  }

  if (props.PopperProps) {
    Object.assign(popperProps, props.PopperProps);
  }

  const callbackProps = {
    opened: open,
    open: () => {
      setOpen(true);
      window.dispatchEvent(
        new CustomEvent("onPopperOpen", {
          detail: {
            sender: ref.current
          }
        })
      );
    },
    close: () => {
      setOpen(false);
    },
    closeWithDelay: () => {
      timer = setTimeout(() => {
        setOpen(false);
      }, 800);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      ref.current &&
      !ref.current.contains(event.target as Node) &&
      popperRef.current &&
      !popperRef.current.contains(event.target as Node)
    ) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const Component = props.component ?? "div";

  return (
    <Component
      ref={ref}
      style={{ cursor: "pointer", ...props.rootStyle }}
      className={props.className}
    >
      {typeof props.children === "function"
        ? props.children(callbackProps)
        : props.children}

      <Popper
        id={id}
        open={open}
        anchorEl={ref.current}
        style={{ zIndex: 10001 }}
        {...popperProps}
        ref={popperRef}
        onMouseEnter={() => {
          if (timer) clearTimeout(timer);
        }}
        onMouseLeave={() => {
          setOpen(false);
        }}
      >
        {typeof props.popperContent === "function"
          ? props.popperContent(callbackProps)
          : props.popperContent}
      </Popper>
    </Component>
  );
}
